// React.
import { useContext, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from "highcharts-react-official";
import api from "../../../api/Base";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { useNavigate } from "react-router-dom";
import { useLicences } from "../../../context/LicenceContext";

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import { TransactionContentionType } from '../../../types/instance/TransactionContentionType';

// Components.
import TransactionContentionTable from '../../../component/instance/table/TransactionsContentionTable';

// Helper.
import {
    fetchResults,
    getConvertedTimeToUTC,
    highchartsCredits
} from '../../../helpers/utils';
import TransactionApi from '../../../api/instance/TransactionApi';
import ConditionalRender from "../../../helpers/ConditionalRender";
import Helper from "../../../helpers/Helper";
import { chartOptionsGranularity } from '../../../helpers/chartOptionsIncreasedGranularity';
import { showError } from '../../../utils/showAlert';

// Constants
import { CHART_DATA_TYPE } from '../../../component/Constants';

export default function TransactionContention(props: {
    instance: InstanceTarget,
    applyPeriod: Function
}) {
    const { instance } = props;
    const timeRangeContext = useContext(TimeRangeContext);

    const [transactions, setTransactions] = useState<TransactionContentionType[]>([]);
    const [timeSeries, setTimeSeries] = useState<number[][]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { licences } = useLicences();

    useEffect(() => {
        if (!(instance.hastransactioncontention && instance.collecttransactioncontention)) {
            navigate(`/not-found`)
        }
    }, [instance.id]);

    const timePeak = useMemo(() => {
        // @ts-ignore
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])


    useEffect(() => {
        async function load() {
            try {
                setLoading(true);
                const transactionsPromises: any[] = []
                const results: any[] = await fetchResults(
                    [
                        TransactionApi.fetchContention(timeRangeContext.getTimeRangeQueryString(), instance.id)
                    ]);
                transactionsPromises.push(api.get(`transaction/contention/time?${timeRangeContext.getTimeRangeQueryString()}&id=${instance.id}`))

                let transactionsData: number[][] = [];
                const responsesTransactions = await Promise.all(transactionsPromises)
                responsesTransactions.forEach((response: { data: any[]; }) => {
                    transactionsData = transactionsData.concat(response.data.map(item => {
                        return [getConvertedTimeToUTC(item), item.contentionduration]
                    }));
                })

                setTimeSeries(transactionsData)

                if (active) {
                    setTransactions(results[0]);
                    setLoading(false);
                }
            } catch (x: any) {
                console.log(x.message);
                const errorMessage = x.message || x.toString();
                setError(errorMessage);
                showError('Failed to load transaction contention', errorMessage);
                setTransactions([]);
                setLoading(false);
            }
        }

        let active: boolean = true;
        void load();
        return () => {
            // Clean up afterwards to avoid race conditions.
            active = false;
        }
    }, [instance.id, timeRangeContext]);

    const timeChart = useMemo(() => {
        const timeChartOptions = chartOptionsGranularity(props.applyPeriod, [
            {
                type: 'column', data: timeSeries, name: 'Contention Duration', zIndex: 1, tooltip: { valueSuffix: 'ms', }
            },
        ], timePeak, {
            credits: { enabled: highchartsCredits(licences) },
            legend: true,
            timeRangeContext: timeRangeContext,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                },
            },
            yAxis: [{
                title: {
                    text: 'Duration (ms)'
                },
                labels: {
                    formatter: function (): string {
                        // @ts-ignore
                        return Helper.getTimeInEnglish(this?.value);
                    }
                },
                min: 0,
                max: timePeak
            }]
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts}
                                options={timeChartOptions}/>;

    }, [timeSeries, timePeak, timeRangeContext, licences]);

    return (<>
        <div className="row">
            <div className="col">
                <div className="card collapsible">
                    <div className="card-header">
                        <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                        Time spent
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseExecutions" aria-expanded="false"
                           aria-controls="collapseExecutions"/>
                    </div>
                    <ConditionalRender if={loading}>
                        <div className="w-100 text-center text-muted mt-3">
                            <div className="loader spinner chartSpinner">
                            </div>
                            <p className="mt-3">
                                Loading data...
                            </p>
                        </div>
                    </ConditionalRender>

                    <ConditionalRender if={!loading}>
                        <div id="collapseExecutions" className="card-body collapse show">
                            {timeChart}
                        </div>
                    </ConditionalRender>
                </div>
            </div>
        </div>

        <div className="row row-cols-1">
            <div id="space" className="tab-pane fade show active" role="tabpanel" aria-labelledby="space-tab">
                <TransactionContentionTable error={error} loading={loading}
                                            data={transactions} instance={instance}/>
            </div>
        </div>
    </>)
}
